import { matchMessageNoiseRule } from '@/shared/lib/errors/noise-rules';
import type { CaptureResult } from 'posthog-js';
import posthog from 'posthog-js';
import { redactSensitiveProperties } from './redact';

const IS_DEV = process.env.NODE_ENV === 'development';

let posthogInitialized = false;

/**
 * Enrich React recoverable-error exceptions (#418 hydration mismatch, #419
 * abandoned Suspense boundary) with delivery context. These errors carry no
 * stack, so without this there is no way to tell a mid-stream connection cut
 * (readyState 'loading', holes still pending) from a post-load failure.
 * Properties are snake_case per the analytics guide §8.
 */
function enrichReactRecoverableError(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$exception') return event;
  const values = JSON.stringify(event.properties?.$exception_list ?? '');
  if (!/#41[89]/.test(values)) return event;

  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } })
    .connection;
  event.properties = {
    ...event.properties,
    delivery_ready_state: document.readyState,
    delivery_visibility: document.visibilityState,
    // PPR holes not yet resumed — React names their placeholders B:<n>
    delivery_pending_holes: document.querySelectorAll('template[id^="B:"]').length,
    delivery_effective_type: connection?.effectiveType ?? null,
    delivery_ms_since_nav: Math.round(performance.now()),
  };
  return event;
}

/**
 * Tag known-noise exceptions (shared message rules in noise-rules.ts) instead
 * of dropping them: `signal: 'noise'` + `noise_rule: <id>` keep them visible
 * for spike detection while default Error Tracking views filter them out.
 * Mirrors the Sentry NOISE_RULES pass in instrumentation-client.ts.
 */
function tagNoiseExceptions(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$exception') return event;
  const list = event.properties?.$exception_list;
  const messages: string[] = Array.isArray(list)
    ? list.map((e: { value?: string }) => e?.value ?? '')
    : [];
  const ruleId = messages.map(matchMessageNoiseRule).find(Boolean);
  if (!ruleId) return event;

  event.properties = {
    ...event.properties,
    signal: 'noise',
    noise_rule: ruleId,
    $exception_level: 'warning',
  };
  return event;
}

/**
 * Idempotent PostHog init, callable from anywhere on the client.
 *
 * Lives outside React on purpose: the auth store's rehydrate → checkAuth →
 * fetchUser chain calls `posthog.identify()` from a network callback that can
 * resolve BEFORE React flushes the provider effect on slow-hydration devices.
 * posthog-js silently drops pre-init calls, leaving logged-in users tracked as
 * anonymous forever (FRONT-273). Callers that capture/identify outside the
 * React lifecycle MUST call this first — `posthog.init` is synchronous, so a
 * call ordered after it can never lose the race.
 */
export function ensurePostHogInit(): void {
  if (
    posthogInitialized ||
    typeof window === 'undefined' ||
    !process.env.NEXT_PUBLIC_POSTHOG_KEY ||
    IS_DEV
  )
    return;
  posthogInitialized = true;
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: '/phtv',
    ui_host: 'https://us.posthog.com',
    defaults: '2026-01-30',
    person_profiles: 'identified_only',

    // Share the distinct_id cookie across taboo.tv subdomains so the landing
    // page (taboo.tv) and app (app.taboo.tv) join into one person and the
    // LP → purchase funnel resolves without URL-param handoffs. LP must also
    // set this. Registrable domain is taboo.tv; same-site so no ITP concerns.
    cross_subdomain_cookie: true,

    // Unhandled exceptions → PostHog Error Tracking. Turned on 2026-07-16 when
    // Sentry's org-wide ingestion stopped and this was the only automatic net
    // left for render/unhandled crashes. Sentry has since recovered (verified
    // 2026-09-16: 104 error events in 24h), so this is now the second sink
    // rather than the only one — kept deliberately, because the outage proved
    // a single error destination is a single point of failure, and this one
    // costs regular event quota rather than a separate plan.
    capture_exceptions: true,
    before_send: [enrichReactRecoverableError, tagNoiseExceptions, redactSensitiveProperties],

    // $pageview: manual — SPA route changes don't trigger a full page load,
    // so PostHogPageViewInner fires it on every pathname change (query-only
    // navigations excluded by design; see posthog-provider.tsx).
    capture_pageview: false,
    // $pageleave: auto. PostHog needs this to compute bounce rate and session
    // duration accurately; without it, sessions look like they never ended.
    // https://posthog.com/docs/libraries/js
    capture_pageleave: true,
    autocapture: false,

    // Session replay: recording start is server-driven (remote config) —
    // sampling, min duration, and URL/event triggers live in PostHog project
    // settings. When the project is over recording quota, the remote config
    // returns quotaLimited:["recordings"] and the SDK never starts recording.
    disable_session_recording: false,
    // Members type emails, passwords, and payment data, and the catalog is
    // sensitive content — recordings must never contain typed text. Sentry
    // Replay already masks (instrumentation-client.ts); this is the PostHog
    // equivalent, which otherwise defaults to unmasked.
    session_recording: {
      maskAllInputs: true,
    },

    // Disable unused features
    disable_surveys: true,
    capture_dead_clicks: false,
    // Web Vitals only (LCP/CLS/FCP/INP) — ~4 events per page session, low volume.
    // network_timing stays off: it fires per-resource and floods ingestion.
    // web_vitals_attribution adds the LCP element/url and the largest CLS
    // shift target to `$web_vitals_*_event` — without it the field data can
    // say a route is slow but not what painted late (FRONT-370).
    capture_performance: { web_vitals: true, web_vitals_attribution: true, network_timing: false },
    enable_heatmaps: false,
    rageclick: false,
    // Scroll depth stays on: it adds `$prev_pageview_*` properties to the
    // `$pageview` / `$pageleave` events we already send — no extra events or
    // requests — and PostHog's Installation Health "Scroll depth" check needs them.
    disable_web_experiments: true,

    // Runtime flags/experiments served by PostHog — registry + hooks in
    // analytics/flags.ts. Build-time env flags stay in config/feature-flags.ts
    // (see the doc's §Feature flags for the split). disable_web_experiments
    // stays on above: no-code experiments are off, variants ship in code.
  });
}
